import { Box, Button, Grid, Typography } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { ItemTypeCheckbox } from "components/FormControls/ItemTypeCheckbox";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TierSetDropdown } from "components/FormControls/TierSetDropdown/TierSetDropdown";
import { Tooltip } from "components/Tooltips/Tooltip";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import {
  Audience,
  AudienceType,
  AudienceTypeUnion,
  TriggerStrategy,
  TriggerStrategyType,
} from "types/campaigns";
import { useStyles } from "../../Campaign.styles";
import { timeBasedTriggers } from "../../campaignHelpers";
import { MonthlySettings } from "./MonthlyTrigger/MonthlySettings";
import { SegmentSelect } from "./SegmentSelect";

interface Props {
  setFieldValue: (field: string, value?: string | string[] | Audience) => void;
  triggerStrategy?: TriggerStrategy;
  audience?: Audience;
  isEditMode?: boolean;
}

export const TimeBasedTriggers = ({
  setFieldValue,
  triggerStrategy,
  audience,
  isEditMode,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();

  const isDailyTrigger = triggerStrategy?.type === TriggerStrategyType.DAILY;
  const isWeeklyTrigger = triggerStrategy?.type === TriggerStrategyType.WEEKLY;
  const isMonthlyTrigger =
    triggerStrategy?.type === TriggerStrategyType.MONTHLY;
  const isEveryoneAudience = audience?.target === AudienceType.EVERYONE;
  const isTierAudience = audience?.target === AudienceType.TIER;
  const isSegmentAudience = audience?.target === AudienceType.SEGMENT;

  const days = [
    { label: message({ id: "mon" }), value: 1 },
    { label: message({ id: "tue" }), value: 2 },
    { label: message({ id: "wed" }), value: 3 },
    { label: message({ id: "thu" }), value: 4 },
    { label: message({ id: "fri" }), value: 5 },
    { label: message({ id: "sat" }), value: 6 },
    { label: message({ id: "sun" }), value: 0 },
  ];

  return (
    <Grid container mt={0} spacing={12}>
      {timeBasedTriggers.map(({ attribute, label, description }) => {
        return (
          <Grid key={attribute} item xs={4}>
            <ItemTypeCheckbox
              activeItem={triggerStrategy?.type === attribute}
              label={message({ id: label })}
              description={message({ id: description })}
              data-test-id={attribute}
              onClick={() => {
                const isBirthdayTrigger =
                  attribute === TriggerStrategyType.BIRTHDAY;
                setFieldValue("triggerStrategy.type", attribute);
                if (isBirthdayTrigger) {
                  return setFieldValue("audience", undefined);
                }
                setFieldValue("audience", { target: AudienceType.EVERYONE });
              }}
              disabled={isEditMode}
            />
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Typography className={classes.link}>
          {message(
            { id: "learnMoreTimeTriggers" },
            {
              link: (
                <ExternalLink path="https://help.openloyalty.io/main-features/campaigns/creating-campaigns/time-based-campaign">
                  {message({ id: "trigersInTimeBasedAutomationsLink" })}
                </ExternalLink>
              ),
            },
          )}
        </Typography>
      </Grid>
      {(isDailyTrigger || isWeeklyTrigger || isMonthlyTrigger) && (
        <>
          <Grid item xs={isEveryoneAudience ? 12 : 6}>
            <SelectInput
              label={<RequiredFieldLabel text={message({ id: "target" })} />}
              data-test-id="audience-target"
              items={[
                {
                  id: AudienceType.EVERYONE,
                  label: message({ id: "everyone" }),
                },
                {
                  id: AudienceType.TIER,
                  label: message({ id: "tier" }),
                },
                {
                  id: AudienceType.SEGMENT,
                  label: message({ id: "segment" }),
                },
              ]}
              name="audience.target"
              onChange={(_e, value: AudienceTypeUnion) => {
                switch (value) {
                  case AudienceType.EVERYONE:
                    setFieldValue("audience.tiers", undefined);
                    setFieldValue("audience.segments", undefined);
                    break;
                  case AudienceType.TIER:
                    return setFieldValue("audience.segments", undefined);
                  case AudienceType.SEGMENT:
                    return setFieldValue("audience.tiers", undefined);
                }
              }}
            />
          </Grid>
          {isTierAudience && (
            <Grid item xs={6}>
              <TierSetDropdown
                name="audience.tiers"
                data-test-id="audience-tiers"
                multiple={false}
                label={message({ id: "targetedTiers" })}
              />
            </Grid>
          )}
          {isSegmentAudience && (
            <Grid item xs={6}>
              <SegmentSelect segments={audience.segments || []} />
            </Grid>
          )}
        </>
      )}
      {isWeeklyTrigger ? (
        <Grid item xs={12}>
          <Typography className={classes.link}>
            {message({ id: "repeatOn" })}
          </Typography>
          <FieldArray
            name="triggerStrategy.executionSchedule.dayOfWeek"
            render={({ push, remove, form: { values, setFieldValue } }) => {
              const selectedDays: number[] =
                values?.triggerStrategy?.executionSchedule?.dayOfWeek;

              return (
                <Box className={classes.days}>
                  {days.map((day) => {
                    const isDaySelected = selectedDays?.includes(day.value);
                    const isDayDisabled =
                      selectedDays?.length === 6 && !isDaySelected;

                    return (
                      <Tooltip
                        text={
                          isDayDisabled ? (
                            <>
                              <Typography className={classes.tooltipTitle}>
                                {message({ id: "areYouSure" })}
                              </Typography>
                              <Typography className={classes.tooltipDesc}>
                                {message({ id: "dailyTriggerSuggestion" })}
                              </Typography>
                              <Button
                                className={classes.tooltipButton}
                                onClick={() =>
                                  setFieldValue("triggerStrategy.type", "daily")
                                }
                              >
                                {message({ id: "changeTriggerToDaily" })}
                              </Button>
                            </>
                          ) : null
                        }
                      >
                        <div>
                          <Box
                            key={day.value}
                            className={`${classes.day} ${isDaySelected ? classes.selectedDay : ""} ${isDayDisabled ? classes.disabledDay : ""}`}
                            onClick={
                              !isDayDisabled
                                ? () => {
                                    const dayIndex = selectedDays?.indexOf(
                                      day.value,
                                    );
                                    if (dayIndex >= 0) {
                                      remove(dayIndex);
                                    } else {
                                      push(day.value);
                                    }
                                  }
                                : undefined
                            }
                          >
                            {day.label.toUpperCase()}
                          </Box>
                        </div>
                      </Tooltip>
                    );
                  })}
                </Box>
              );
            }}
          />
        </Grid>
      ) : null}

      {isMonthlyTrigger && (
        <Grid item xs={12}>
          <MonthlySettings
            setFieldValue={setFieldValue}
            dayOfMonth={triggerStrategy.executionSchedule?.dayOfMonth || []}
          />
        </Grid>
      )}
    </Grid>
  );
};
